import { FCElement } from '@fc/component';
import { debounce } from '@fc/utilities';

class MarqueeComponent extends FCElement {
  requiredRefs = ['wrapper', 'content'];

  #ac = null;
  #anim = null;
  #focusPaused = false;

  static FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  static ANIMATION_OPTIONS = {
    duration: 500,
  };

  connectedCallback() {
    super.connectedCallback();

    this.#ac?.abort();
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const { content } = this.refs;

    if (!content.firstElementChild?.children.length) return;

    this.#prepareStaticFocusPath();
    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    window.addEventListener('resize', this.#handleResize, { signal, passive: true });
    this.addEventListener('pointerenter', this.#slowDown, { signal, passive: true });
    this.addEventListener('pointerleave', this.#speedUp, { signal, passive: true });
    this.addEventListener('focusin', this.#pauseForFocus, { signal });
    this.addEventListener('focusout', this.#resumeAfterFocus, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#anim?.cancel();
    this.#anim = null;
    this.#ac?.abort();
    this.#ac = null;
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const last = wrapper.lastElementChild;
    return content !== last && last instanceof HTMLElement ? last : null;
  }

  #slowDown = debounce(() => {
    if (this.#focusPaused) return;
    if (this.#anim) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#anim = animateValue({
      ...MarqueeComponent.ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (v) => animation.updatePlaybackRate(v),
      onComplete: () => {
        this.#anim = null;
      },
    });
  }, MarqueeComponent.ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    if (this.#focusPaused) return;
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#anim?.current ?? 0;
    this.#anim?.cancel();

    this.#anim = animateValue({
      ...MarqueeComponent.ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (v) => animation.updatePlaybackRate(v),
      onComplete: () => {
        this.#anim = null;
      },
    });
  };

  #pauseForFocus = () => {
    this.#focusPaused = true;
    this.#slowDown.cancel();
    this.#anim?.cancel();
    this.#anim = null;

    this.refs.wrapper.style.animation = 'none';
    this.refs.wrapper.style.transform = 'translate3d(0, 0, 0)';
  };

  #resumeAfterFocus = (event) => {
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.#focusPaused = false;
    this.refs.wrapper.style.removeProperty('animation');
    this.refs.wrapper.style.removeProperty('transform');
    this.#restartAnimation();
  };

  #setSpeed(value = this.#calcSpeed()) {
    if (typeof value !== 'number' || value <= 0 || !isFinite(value)) return;
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calcSpeed() {
    const factor = Number(this.getAttribute('data-speed-factor')) || 50;
    const w = this.offsetWidth;
    if (factor <= 0 || w <= 0) return 1;
    return Math.max(1, Math.ceil(w / factor / 2));
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const needed = this.#calcCopies();
    const current = content.children.length;

    if (needed > current) {
      this.#addRepeatedItems(needed - current);
    } else if (needed < current) {
      this.#removeRepeatedItems(current - needed);
    }

    this.#duplicateContent();
    this.#setSpeed();
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const anims = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const a of anims) a.currentTime = 0;
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = this.refs.content.cloneNode(true);
    if (!(clone instanceof HTMLElement)) return;

    clone.removeAttribute('ref');
    this.#makeDecorativeClone(clone);
    this.refs.wrapper.appendChild(clone);
  }

  #prepareStaticFocusPath() {
    const { content, wrapper } = this.refs;
    const source = content.firstElementChild;
    if (!(source instanceof HTMLElement)) return;

    this.querySelector(':scope > .marquee__static-focus-path')?.remove();

    const focusables = Array.from(source.querySelectorAll(MarqueeComponent.FOCUSABLE_SELECTOR));
    if (focusables.length === 0) return;

    const staticPath = document.createElement('div');
    staticPath.className = 'marquee__static-focus-path';
    staticPath.setAttribute('data-marquee-static-focus-path', '');

    const staticItems = source.cloneNode(true);
    if (!(staticItems instanceof HTMLElement)) return;

    this.#restoreStaticFocusables(staticItems);
    staticPath.appendChild(staticItems);
    wrapper.before(staticPath);

    this.#makeAnimatedFocusablesDecorative(source);
  }

  #addRepeatedItems(count = this.#calcCopies()) {
    const { content } = this.refs;
    const source = content.firstElementChild;
    if (!(source instanceof HTMLElement)) return;

    const copies = Math.max(0, Math.floor(count));
    for (let i = 0; i < copies - 1; i++) {
      const clone = source.cloneNode(true);
      if (!(clone instanceof HTMLElement)) continue;
      this.#makeDecorativeClone(clone);
      content.appendChild(clone);
    }
  }

  #makeDecorativeClone(node) {
    node.setAttribute('aria-hidden', 'true');

    for (const el of node.querySelectorAll(MarqueeComponent.FOCUSABLE_SELECTOR)) {
      el.setAttribute('tabindex', '-1');
    }
  }

  #makeAnimatedFocusablesDecorative(node) {
    for (const el of node.querySelectorAll(MarqueeComponent.FOCUSABLE_SELECTOR)) {
      if (!el.hasAttribute('data-marquee-original-tabindex')) {
        el.setAttribute('data-marquee-original-tabindex', el.getAttribute('tabindex') ?? '');
      }

      if (!el.hasAttribute('data-marquee-original-aria-hidden')) {
        el.setAttribute('data-marquee-original-aria-hidden', el.getAttribute('aria-hidden') ?? '');
      }

      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-hidden', 'true');
    }
  }

  #restoreStaticFocusables(node) {
    node.removeAttribute('aria-hidden');
    node.removeAttribute('inert');

    for (const el of node.querySelectorAll(MarqueeComponent.FOCUSABLE_SELECTOR)) {
      const originalTabindex = el.getAttribute('data-marquee-original-tabindex');
      const originalAriaHidden = el.getAttribute('data-marquee-original-aria-hidden');

      if (originalTabindex === '') {
        el.removeAttribute('tabindex');
      } else if (originalTabindex !== null) {
        el.setAttribute('tabindex', originalTabindex);
      }

      if (originalAriaHidden === '') {
        el.removeAttribute('aria-hidden');
      } else if (originalAriaHidden !== null) {
        el.setAttribute('aria-hidden', originalAriaHidden);
      }

      el.removeAttribute('data-marquee-original-tabindex');
      el.removeAttribute('data-marquee-original-aria-hidden');
    }
  }

  #removeRepeatedItems(count) {
    const { content } = this.refs;
    const n = Math.max(0, Math.floor(count));
    for (let i = 0; i < n; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calcCopies() {
    const { content } = this.refs;
    const w = this.offsetWidth;
    const first = content.firstElementChild;
    const itemW = first instanceof HTMLElement ? first.offsetWidth : 1;
    if (w <= 0 || itemW <= 0) return 1;
    return Math.max(1, Math.ceil(w / itemW));
  }
}

// Smoothly animates a numeric value over time using rAF
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const start = performance.now();
  let stopped = false;
  let cur = from;
  let raf = null;

  const tick = (now) => {
    if (stopped) return;

    const progress = Math.min((now - start) / duration, 1);
    cur = from + (to - from) * easing(progress);
    onUpdate(cur);

    if (progress < 1) {
      raf = requestAnimationFrame(tick);
    } else {
      onComplete?.();
    }
  };

  raf = requestAnimationFrame(tick);

  return {
    get current() {
      return cur;
    },
    cancel() {
      stopped = true;
      if (raf !== null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    },
  };
}

if (!customElements.get('text-marquee')) {
  customElements.define('text-marquee', MarqueeComponent);
}
